import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import nohBannerUrl from '../assets/national-open-house-banner.gif';

/**
 * National Open House campaign strip.
 * Hidden automatically at the start of 2026-10-25 (America/Chicago).
 */
const NOH_EXPIRES_AT_MS = Date.parse('2026-10-25T05:00:00.000Z');

const linkDef: SlotGroupDefinition = {
  label: 'Banner Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Label',
      type: 'text',
      defaultValue: 'National Open House',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function setNohBannerHeight(px: number) {
  document.documentElement.style.setProperty('--noh-banner-height', `${px}px`);
}

function isNohActive(): boolean {
  return Date.now() < NOH_EXPIRES_AT_MS;
}

export function NationalOpenHouseBanner() {
  const alt = useContentToken('nationalopenhousebanner.alt');
  const link = useSlotGroup('nationalopenhousebanner.link', linkDef);
  const bannerRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useRef(0);
  const [scrolledAway, setScrolledAway] = useState(false);

  const active = isNohActive();
  const isVisible = active && !scrolledAway;
  const href = typeof link.url === 'string' ? link.url.trim() : '';
  const hasLink = href.length > 0;

  // Measure natural height; publish 0 while scrolled away so header/offer banner reflow.
  useEffect(() => {
    if (!active || !bannerRef.current) {
      setNohBannerHeight(0);
      return;
    }

    const measure = () => {
      if (!bannerRef.current) return;
      measuredHeight.current = bannerRef.current.scrollHeight;
      setNohBannerHeight(isVisible ? measuredHeight.current : 0);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bannerRef.current);
    return () => {
      ro.disconnect();
      setNohBannerHeight(0);
    };
  }, [active, isVisible]);

  useEffect(() => {
    setNohBannerHeight(isVisible ? measuredHeight.current : 0);
  }, [isVisible]);

  // Scroll-based auto-hide with hysteresis to prevent bouncing
  useEffect(() => {
    if (!active) return;
    let ticking = false;
    const HIDE_AT = 200;
    const SHOW_AT = 50;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolledAway((prev) => {
          if (!prev && y > HIDE_AT) return true;
          if (prev && y < SHOW_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [active]);

  if (!active) return null;

  const image = (
    <EditableToken path="nationalopenhousebanner.alt" as="div">
      <img
        src={nohBannerUrl}
        alt={alt || 'National Open House'}
        className="noh-img"
        width={1920}
        height={120}
        decoding="async"
      />
    </EditableToken>
  );

  return (
    <div
      ref={bannerRef}
      className="noh"
      data-visible={isVisible}
      role="banner"
      aria-label="National Open House"
      aria-hidden={!isVisible}
    >
      <EditableSlotGroup groupPath="nationalopenhousebanner.link" definition={linkDef}>
        {hasLink ? (
          <a
            href={href}
            target={link._target || '_self'}
            rel={(link._target || '_self') === '_blank' ? 'noopener noreferrer' : undefined}
            className="noh-link"
            aria-label={link.label || 'National Open House'}
            tabIndex={isVisible ? undefined : -1}
          >
            {image}
          </a>
        ) : (
          <span className="noh-link noh-link--static">{image}</span>
        )}
      </EditableSlotGroup>

      <style>{`
        .noh {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 61;
          line-height: 0;
          background: #1a120c;
          transform: translateY(-100%);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.35s ease;
        }
        .noh[data-visible="true"] {
          transform: translateY(0);
          opacity: 1;
          pointer-events: auto;
        }
        .noh-link {
          display: block;
        }
        .noh-link--static {
          pointer-events: none;
        }
        .noh-img {
          display: block;
          width: 100%;
          height: auto;
          max-height: 120px;
          object-fit: cover;
          object-position: center;
        }
      `}</style>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NationalOpenHouseBanner',
  displayName: 'National Open House Banner',
  tokens: {
    'nationalopenhousebanner.alt': {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'National Open House',
    },
  },
  slotGroups: {
    'nationalopenhousebanner.link': linkDef,
  },
};

(NationalOpenHouseBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NationalOpenHouseBanner;
